import axios from 'axios';
import { normalizeContactData } from '../utils/contactDataUtils';
import { notifyUnauthorized } from './authEvents';

// No default Content-Type: uploads go as multipart (the browser adds the boundary), the rest as JSON.
// Reading PDFs takes a few seconds each, so allow longer than the other API calls.
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  timeout: 120000
});

// The session ended or never started: back to the sign-in page
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) notifyUnauthorized();
    return Promise.reject(error);
  }
);

// Error carrying the API's message. A 409 that needs the user to look again (the person changed,
// or the PDF needs confirming) also carries the fresh comparison as `review`.
const toError = (error, fallbackMessage) => {
  const detail = error.response?.data?.detail;
  const message = typeof detail === 'string' ? detail : detail?.message;
  const apiError = new Error(message || error.message || fallbackMessage);
  apiError.status = error.response?.status;
  apiError.review = detail && typeof detail === 'object' ? detail.review || null : null;
  apiError.originalError = error;
  return apiError;
};

const pdfForm = (field, files) => {
  const form = new FormData();
  files.forEach(file => form.append(field, file, file.name));
  return form;
};

/**
 * LinkedIn profile PDFs: read one for a person, or several for a company's people, compare each
 * with what is on file, then save. A comparison ("review") lists the verdict, field-by-field
 * comparison, the changes saving makes, and the plan_hash that saving needs back.
 */
class ProfileApiService {

  /**
   * Read a profile PDF for one person; nothing is saved yet
   * @param {number} contactId - Contact ID
   * @param {File} file - The PDF
   * @returns {Promise<Object>} import_id, file_name, profile and the review
   */
  async uploadContactPdf(contactId, file) {
    try {
      const { data } = await apiClient.post(`/api/v2/contacts/${contactId}/linkedin-pdf`, pdfForm('file', [file]));
      return data;
    } catch (error) {
      throw toError(error, 'Could not read the PDF');
    }
  }

  /**
   * Save a read PDF to the person it was read for
   * @param {number} contactId - Contact ID
   * @param {number} importId - import_id from the upload
   * @param {string} planHash - plan_hash of the review the user saw
   * @param {boolean} confirmSamePerson - The user confirmed a 'mismatch' is the same person
   * @returns {Promise<Object>} saved, backup and the person as now on file (normalised)
   */
  async saveContactPdf(contactId, importId, planHash, confirmSamePerson = false) {
    try {
      const { data } = await apiClient.post(`/api/v2/contacts/${contactId}/linkedin-pdf/${importId}/apply`, {
        plan_hash: planHash,
        confirm_same_person: confirmSamePerson
      });
      return { ...data, contact: normalizeContactData(data.contact) };
    } catch (error) {
      throw toError(error, 'Could not save the PDF');
    }
  }

  /**
   * LinkedIn PDFs saved to a person, newest first
   * @param {number} contactId - Contact ID
   * @returns {Promise<Array<Object>>}
   */
  async getContactHistory(contactId) {
    try {
      const { data } = await apiClient.get(`/api/v2/contacts/${contactId}/linkedin-pdf`);
      return Array.isArray(data?.imports) ? data.imports : [];
    } catch (error) {
      throw toError(error, 'Could not load saved PDFs');
    }
  }

  /**
   * Read several profile PDFs for people at a company; each is matched to someone there
   * @param {number} companyId - Company ID
   * @param {File[]} files - The PDFs
   * @returns {Promise<Object>} people (everyone at the company) and files (per PDF: proposed review, alternatives or error)
   */
  async uploadCompanyPdfs(companyId, files) {
    try {
      const { data } = await apiClient.post(`/api/v2/companies/${companyId}/linkedin-pdfs`, pdfForm('files', files));
      return data;
    } catch (error) {
      throw toError(error, 'Could not read the PDFs');
    }
  }

  /**
   * Read several profile PDFs of people at any company ("Add people from PDFs"); each is matched
   * against everyone on file, or offered as a new person at the PDF's current employer
   * @param {File[]} files - The PDFs
   * @returns {Promise<Object>} files: per PDF candidates, proposed review, alternatives, new_person or error
   */
  async uploadPdfs(files) {
    try {
      const { data } = await apiClient.post('/api/v2/linkedin-pdfs', pdfForm('files', files));
      return data;
    } catch (error) {
      throw toError(error, 'Could not read the PDFs');
    }
  }

  /**
   * Compare a read PDF with another person
   * @param {number} importId - import_id from the upload
   * @param {number} contactId - The person
   * @returns {Promise<Object>} The review
   */
  async reviewImport(importId, contactId) {
    try {
      const { data } = await apiClient.get(`/api/v2/linkedin-pdf-imports/${importId}/review`, { params: { contact_id: contactId } });
      return data;
    } catch (error) {
      throw toError(error, 'Could not compare the PDF');
    }
  }

  /**
   * Save several read PDFs, each to the person chosen for it or as a new person ('panel': at the
   * company they were uploaded for, 'current': at the PDF's current employer); items succeed or
   * fail one by one
   * @param {Array<{importId: number, contactId?: number, newPerson?: string, planHash: string, confirmSamePerson?: boolean}>} items
   * @returns {Promise<Object>} saved, failed, backup and per-item results
   */
  async saveImports(items) {
    try {
      const { data } = await apiClient.post('/api/v2/linkedin-pdf-imports/apply', {
        items: items.map(item => ({
          import_id: item.importId,
          ...(item.newPerson ? { new_person: item.newPerson } : { contact_id: item.contactId }),
          plan_hash: item.planHash,
          confirm_same_person: Boolean(item.confirmSamePerson)
        }))
      });
      return data;
    } catch (error) {
      throw toError(error, 'Could not save the PDFs');
    }
  }

  /**
   * Changes to a person, newest first: who made each, when, and each field's before and after
   * @param {number} contactId - Contact ID
   * @param {number} [beforeId] - next_before_id of the previous page
   * @returns {Promise<{events: Array<Object>, next_before_id: ?number}>}
   */
  async getChangeHistory(contactId, beforeId) {
    try {
      const { data } = await apiClient.get(`/api/v2/contacts/${contactId}/history`, {
        params: beforeId ? { before_id: beforeId, limit: 20 } : { limit: 20 }
      });
      return data;
    } catch (error) {
      throw toError(error, 'Could not load the history');
    }
  }

  /**
   * Drop a read PDF that won't be saved; unsaved ones also expire after a day
   * @param {number} importId - import_id from the upload
   */
  async discardImport(importId) {
    try {
      await apiClient.delete(`/api/v2/linkedin-pdf-imports/${importId}`);
    } catch (error) {
      // Already saved, expired or gone: nothing left to drop
    }
  }
}

const profileApi = new ProfileApiService();

export default profileApi;
